import type { ReactNode } from "react";
import type { AutomationStatsDto } from "../../shared/dto";
import { AutomationTimingEstimate } from "../AutomationTimingEstimate";
import { canonicalLifecycle } from "./lifecycle";

export type PhaseWorkspaceKind =
  | "overview"
  | "call"
  | "profile"
  | "questionnaire"
  | "knowledge"
  | "training"
  | "beta"
  | "feedback"
  | "material"
  | "stripe"
  | "payments"
  | "publication"
  | "handover"
  | "app";

export type StaffWorkState =
  | "partner_action"
  | "automation"
  | "waiting_client"
  | "blocked"
  | "locked"
  | "complete";

// The single source of the step-to-material mapping is the canonical truth
// module; the workspace only re-exports it (fixr4).
import {
  STEP_MATERIAL_KINDS,
  materialKindsForStep as sharedMaterialKindsForStep
} from "../../shared/staff-lifecycle";

const MATERIAL_KINDS = STEP_MATERIAL_KINDS;

/**
 * Every rail step maps to the workspace that belongs to IT. Steps missing from
 * this table fell through to `overview`, which rendered the entire old console
 * (journey card + videos + DFY + know-how uploader + Stripe) under a heading
 * that said "Úvodní hovor" (audit P0). The rule now: a step with no workspace
 * of its own is a bug, not a fallback.
 */
const WORKSPACE_KINDS: Record<string, PhaseWorkspaceKind> = {
  onboarding_call: "call",
  marketing_call: "call",
  profile: "profile",
  q1: "questionnaire",
  q2: "questionnaire",
  knowhow: "knowledge",
  training: "training",
  beta: "beta",
  feedback: "feedback",
  strategy: "material",
  salespage: "material",
  emails: "material",
  webinar: "material",
  stripe: "stripe",
  payments: "payments",
  salespage_publish: "publication",
  handover: "handover",
  apple: "app",
  app_questionnaire: "app",
  app_release: "app"
};

/** Which stable call link belongs to a call step. */
export function callLinkKindForStep(step: string): "onboarding" | "marketing" | null {
  if (step === "onboarding_call") return "onboarding";
  if (step === "marketing_call") return "marketing";
  return null;
}

export function phaseWorkspaceKind(step: string): PhaseWorkspaceKind {
  return WORKSPACE_KINDS[step] ?? "overview";
}

export function materialKindsForStep(
  step: string,
  deliverables?: Array<{ kind?: unknown }>
): string[] {
  return sharedMaterialKindsForStep(step, deliverables);
}

export function materialWorkState(input: {
  deliverable: { status: string; published: boolean } | null;
  openFeedback: number;
  runStatus: string | null;
}): StaffWorkState {
  const releasable = input.deliverable
    && !input.deliverable.published
    && ["review", "approved"].includes(input.deliverable.status);
  return canonicalLifecycle({
    failed: input.runStatus === "failed",
    working: input.runStatus === "running",
    readyForPartner: input.openFeedback > 0 || Boolean(releasable),
    waitingClient: !input.deliverable,
    complete: Boolean(input.deliverable?.published && input.deliverable.status === "approved")
  }).workState;
}

/**
 * What the material card says about the BUILD, independent of whether a
 * document exists yet.
 *
 * Reported by Pavel 2026-08-03: a failed sales page build that never produced
 * anything showed the chip "Zablokované" above the words "Pro tuto fázi zatím
 * není připravený materiál" - no reason, nothing to click. The failure notice
 * and its retry button had been written inside the branch that renders only
 * once a document exists, which is exactly when they are least needed. A build
 * that failed before producing anything is the case that MOST needs an
 * explanation and a way out.
 */
export function materialBuildNotices(input: {
  runStatus: string | null;
  hasDeliverable: boolean;
}): { running: boolean; failure: boolean; emptyText: "waiting" | "none" | null } {
  const running = input.runStatus === "running";
  const failure = input.runStatus === "failed";
  if (input.hasDeliverable) return { running, failure, emptyText: null };
  // A failed run already explained itself and offered the retry; repeating
  // "nothing is ready yet" underneath reads as a second, contradictory reason.
  if (failure) return { running, failure, emptyText: null };
  return { running, failure, emptyText: running ? "waiting" : "none" };
}

/**
 * What the review room shows as THE material. A deliverable with a real page
 * URL is that page; the builder also posts a short internal summary (file
 * paths, workdir notes) as contentMd, and preferring it showed the summary
 * document instead of the sales page (Pavel 2026-08-04). The summary is build
 * metadata, never the material.
 */
export function materialViewSource(
  url: string | null | undefined,
  contentMd: string | null | undefined
): { mode: "page"; url: string } | { mode: "doc"; contentMd: string } | { mode: "empty" } {
  const trimmed = (url ?? "").trim();
  const inline = typeof contentMd === "string" ? contentMd.trimStart() : "";
  // A deliverable whose content IS a web page (stored html or a live URL)
  // reviews as a PAGE; documents review as documents.
  if (/^<!doctype|^<html/i.test(inline)) return { mode: "page", url: trimmed };
  if (/^https?:\/\//.test(trimmed)) return { mode: "page", url: trimmed };
  if (typeof contentMd === "string" && contentMd) return { mode: "doc", contentMd };
  return { mode: "empty" };
}

export function activeAutomationKindForStep(
  step: string,
  activeKinds: readonly string[],
  salesEditActive = false
): string | undefined {
  if (salesEditActive && MATERIAL_KINDS[step]) return "salesedit";
  const candidates: Record<string, string[]> = {
    profile: ["profile_research", "map_apply"],
    knowhow: ["knowhow_provisioning"],
    training: ["training"],
    stripe: ["stripe_connect_autolink"],
    app_questionnaire: ["app_wizard_prefill"]
  };
  const active = new Set(activeKinds);
  return candidates[step]?.find((kind) => active.has(kind));
}

export function PhaseWorkspaceFrame(props: {
  step: string;
  state: StaffWorkState;
  t: (key: string) => string;
  automationStats?: AutomationStatsDto;
  activeAutomationKind?: string;
  children?: ReactNode;
}) {
  const kind = phaseWorkspaceKind(props.step);
  // Material steps render a review card that carries the SAME state chip a
  // few pixels lower. Printing it twice made the eye check whether the two
  // agreed; the card owns it, the frame stays quiet (audit P1).
  const chipBelongsToCard = kind === "material";
  const state = props.activeAutomationKind ? "automation" : props.state;
  return (
    <section
      className={`staff-phase-workspace state-${state}`}
      data-phase-workspace={kind}
      data-work-state={state}
      data-active-automation={props.activeAutomationKind}
    >
      <header className="staff-phase-workspace-head">
        <div>
          <p className="panel-label">{props.t("partnerOS.workspace.kicker")}</p>
          <h2>{props.t(`rail.labels.${props.step}`)}</h2>
        </div>
        {!chipBelongsToCard && (
          <div className="staff-work-state-wrap">
            <span className={`staff-work-state ${state}`}>
              {props.t(`partnerOS.workState.${state}`)}
            </span>
            {state === "automation" && (
              <AutomationTimingEstimate stats={props.automationStats} t={props.t} />
            )}
          </div>
        )}
      </header>
      <div className="staff-phase-workspace-body">{props.children}</div>
    </section>
  );
}
